import React from 'react';
import { FlightAnalysis } from '../types';
import { useCurrency } from '../contexts/CurrencyContext';
import { formatPrice } from '../utils/currency';
import { getCityNameFromAirportCode } from '../utils/airportCodes';
import './CostEstimate.css';

interface CostEstimateProps {
  data: FlightAnalysis;
}

const CostEstimate: React.FC<CostEstimateProps> = ({ data }) => {
  const { currency, currencyInfo } = useCurrency();
  const displayCurrency = data.currency || currency;
  const savings = data.estimatedTotal - data.basePrice;
  const percentageIncrease = data.basePrice > 0 
    ? ((savings / data.basePrice) * 100).toFixed(1)
    : '0';

  // Format flight information display
  const formatFlightInfo = () => {
    const parts: string[] = [];
    
    if (data.flightNumber) {
      parts.push(data.flightNumber);
    }
    
    if (data.route?.origin && data.route?.destination) {
      const originCity = getCityNameFromAirportCode(data.route.origin);
      const destCity = getCityNameFromAirportCode(data.route.destination);
      parts.push(`${originCity} to ${destCity}`);
    }
    
    if (data.airline) {
      parts.push(data.airline);
    }
    
    return parts.length > 0 ? parts.join(' | ') : null;
  };

  // Debug logging to verify what we're rendering
  console.log('[CostEstimate] Rendering with includedServices:', JSON.stringify(data.includedServices));
  console.log('[CostEstimate] checkedBaggage:', data.includedServices?.checkedBaggage);
  console.log('[CostEstimate] carryOn:', data.includedServices?.carryOn);
  console.log('[CostEstimate] meals:', data.includedServices?.meals);
  console.log('[CostEstimate] seatSelection:', data.includedServices?.seatSelection);
  console.log('[CostEstimate] Flight data:', { flightNumber: data.flightNumber, airline: data.airline, route: data.route });

  // Show error if base price is 0
  if (data.basePrice === 0) {
    return (
      <div className="cost-estimate">
        <div className="estimate-card">
          <div className="estimate-header">
            <h2>True Cost Estimate</h2>
            <p className="currency-indicator">
              All prices in {displayCurrency} {currencyInfo?.symbol ? `(${currencyInfo.symbol})` : ''}
            </p>
            {formatFlightInfo() && <p className="airline-name">{formatFlightInfo()}</p>}
          </div>
          <div className="error-message" style={{ padding: '20px', textAlign: 'center', color: '#d32f2f' }}>
            <strong>Error: Unable to fetch base price</strong>
            <p>We were unable to retrieve the base price for this flight. Please enter the base price manually in the input field above.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cost-estimate">
      <div className="estimate-card">
        <div className="estimate-header">
          <h2>True Cost Estimate</h2>
          <p className="currency-indicator">
            All prices in {displayCurrency} {currencyInfo?.symbol ? `(${currencyInfo.symbol})` : ''}
          </p>
          {formatFlightInfo() && <p className="airline-name">{formatFlightInfo()}</p>}
          {data.dataSource?.fees === 'fallback' && (
            <div className="fallback-notice" style={{
              marginTop: '10px',
              padding: '10px',
              backgroundColor: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '4px',
              fontSize: '0.9em',
              color: '#856404'
            }}>
              ⚠️ <strong>Approximate Pricing:</strong> Exact pricing for optional extras, is currently unavailable for this flight number. Final pricing may vary.
            </div>
          )}
        </div>
        
        <div className="price-comparison">
          <div className="price-item base-price">
            <span className="price-label">Base Price</span>
            <span className="price-value">{formatPrice(data.basePrice, displayCurrency)}</span>
          </div>
          
          <div className="price-item additional-costs">
            <span className="price-label">Additional Costs</span>
            <span className="price-value">+{formatPrice(savings, displayCurrency)}</span>
            <span className="price-percentage">({percentageIncrease}% more)</span>
          </div>
          
          <div className="price-item total-price">
            <span className="price-label">Estimated Total</span>
            <span className="price-value">{formatPrice(data.estimatedTotal, displayCurrency)}</span>
          </div>
        </div>

        <div className="services-breakdown">
          <div className="included-services">
            <h3>What's Included in Base Price</h3>
            <div className="services-list">
              {/* Personal item is always included for budget airlines */}
              <div className="service-item included">
                <span className="service-icon">✓</span>
                <span>Personal Item (small bag under seat)</span>
              </div>
              {/* Use actual includedServices flags from API instead of inferring from breakdown */}
              {/* Breakdown costs can be 0 when pricing data is unavailable, which doesn't mean included */}
              {data.includedServices?.checkedBaggage && (
                <div className="service-item included">
                  <span className="service-icon">✓</span>
                  <span>Checked Baggage</span>
                </div>
              )}
              {data.includedServices?.carryOn && (
                <div className="service-item included">
                  <span className="service-icon">✓</span>
                  <span>Carry-on Baggage</span>
                </div>
              )}
              {data.includedServices?.seatSelection && (
                <div className="service-item included">
                  <span className="service-icon">✓</span>
                  <span>Seat Selection</span>
                </div>
              )}
              {data.includedServices?.meals && (
                <div className="service-item included">
                  <span className="service-icon">✓</span>
                  <span>Meals</span>
                </div>
              )}
            </div>
          </div>

          <div className="not-included-services">
            <h3>What's Not Included (Costs Extra)</h3>
            <div className="services-list">
              {/* Show all services that are NOT included, regardless of pricing availability */}
              {/* If pricing is available, show normally; if not, indicate pricing is unavailable */}
              {!data.includedServices?.checkedBaggage && (
                <div className="service-item not-included">
                  <span className="service-icon">✗</span>
                  <span>
                    Checked Baggage
                    {!(data.breakdown.checkedBaggage && data.breakdown.checkedBaggage > 0) && (
                      <span style={{ fontStyle: 'italic', opacity: 0.7, marginLeft: '8px' }}>
                        (pricing unavailable)
                      </span>
                    )}
                  </span>
                </div>
              )}
              {!data.includedServices?.carryOn && (
                <div className="service-item not-included">
                  <span className="service-icon">✗</span>
                  <span>
                    Carry-on Baggage
                    {!(data.breakdown.carryOn && data.breakdown.carryOn > 0) && (
                      <span style={{ fontStyle: 'italic', opacity: 0.7, marginLeft: '8px' }}>
                        (pricing unavailable)
                      </span>
                    )}
                  </span>
                </div>
              )}
              {!data.includedServices?.seatSelection && (
                <div className="service-item not-included">
                  <span className="service-icon">✗</span>
                  <span>
                    Seat Selection
                    {!(data.breakdown.seatSelection && data.breakdown.seatSelection > 0) && (
                      <span style={{ fontStyle: 'italic', opacity: 0.7, marginLeft: '8px' }}>
                        (pricing unavailable)
                      </span>
                    )}
                  </span>
                </div>
              )}
              {!data.includedServices?.meals && (
                <div className="service-item not-included">
                  <span className="service-icon">✗</span>
                  <span>
                    Meals
                    {!(data.breakdown.meals && data.breakdown.meals > 0) && (
                      <span style={{ fontStyle: 'italic', opacity: 0.7, marginLeft: '8px' }}>
                        (pricing unavailable)
                      </span>
                    )}
                  </span>
                </div>
              )}
              {(data.breakdown.publicTransportCost && data.breakdown.publicTransportCost > 0) && (
                <div className="service-item not-included">
                  <span className="service-icon">✗</span>
                  <span>Additional Transport Costs</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="quick-stats">
          <div className="stat-item">
            <span className="stat-label">Delay Risk</span>
            <span className={`stat-value risk-${data.delayLikelihood}`}>
              {data.delayLikelihood.toUpperCase()}
            </span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Cancellation Risk</span>
            <span className={`stat-value risk-${data.cancellationLikelihood}`}>
              {data.cancellationLikelihood.toUpperCase()}
            </span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Service Rating</span>
            <span className="stat-value">
              {data.customerServiceRating.toFixed(1)}/5.0
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CostEstimate;

